import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface ServiceCard {
  slug: string;
  name: string;
  shortDesc: string;
  icon: string;
  image: string;
}

interface ServicesOverviewProps {
  title?: string;
  subtitle?: string;
  services: ServiceCard[];
  ctaLabel?: string;
  ctaHref?: string;
  basePath?: string;
}

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function ServicesOverview({
  title = 'Event & Program Services',
  subtitle = 'End-to-end automotive event support designed for brands, agencies, and programs that demand zero compromise.',
  services,
  ctaLabel = 'View All Services',
  ctaHref = '/services',
  basePath = '/services',
}: ServicesOverviewProps) {
  const { ref, visible } = useReveal();

  return (
    <section ref={ref} className="py-28 px-6 lg:px-12 xl:px-16 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-gold" />
              <span className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold">What We Do</span>
            </div>
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold leading-tight max-w-xl">
              {title}
            </h2>
          </div>
          <p className="text-white/45 text-base font-sans leading-relaxed max-w-md lg:text-right">
            {subtitle}
          </p>
        </div>

        {/* Service Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
          {services.map((svc, i) => (
            <Link
              key={svc.slug}
              to={`${basePath}/${svc.slug}`}
              className={`group relative overflow-hidden bg-[#0d0d0d] cursor-pointer transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={svc.image}
                  alt={svc.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-black/40 to-transparent" />
                <div className="absolute top-5 left-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/15 border border-gold/30">
                    <i className={`${svc.icon} text-gold text-lg`} />
                  </div>
                </div>
              </div>

              {/* Text */}
              <div className="p-7">
                <h3 className="font-serif text-white text-xl font-semibold mb-3 group-hover:text-gold transition-colors duration-300 leading-snug">
                  {svc.name}
                </h3>
                <p className="text-white/45 text-sm font-sans leading-relaxed mb-5">
                  {svc.shortDesc}
                </p>
                <div className="flex items-center gap-2 text-gold/60 group-hover:text-gold text-xs font-sans font-semibold tracking-widest uppercase transition-colors duration-300">
                  <span>Learn More</span>
                  <div className="w-4 h-4 flex items-center justify-center transform group-hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-arrow-right-line text-sm" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className={`flex justify-center mt-16 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Link
            to={ctaHref}
            className="border border-gold/40 hover:bg-gold hover:text-[#0A0A0A] text-gold font-sans font-semibold text-sm px-10 py-4 uppercase tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            {ctaLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}
